import React from 'react';
import { motion } from 'framer-motion';

// Shimmer effect for loading states
const shimmer = `
  relative overflow-hidden
  before:absolute before:inset-0
  before:-translate-x-full
  before:animate-shimmer
  before:bg-gradient-to-r
  before:from-transparent before:via-white/10 before:to-transparent
`;

// Base skeleton component
const SkeletonBase = ({ className = '', animate = true }) => (
  <div 
    className={`
      bg-[#0F0F16] rounded-xl
      ${animate ? shimmer : ''}
      ${className}
    `}
  />
);

// Text skeleton
export const SkeletonText = ({ lines = 1, className = '' }) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <SkeletonBase 
        key={i} 
        className={`h-4 ${i === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full'}`} 
      />
    ))}
  </div>
);

// Avatar skeleton
export const SkeletonAvatar = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24'
  };
  
  return <SkeletonBase className={`${sizes[size]} rounded-full ${className}`} />;
};

// Card skeleton
export const SkeletonCard = ({ className = '' }) => (
  <div className={`p-4 rounded-2xl bg-[#08080C] border border-white/5 ${className}`}>
    <div className="flex items-center gap-3 mb-4">
      <SkeletonAvatar size="md" />
      <div className="flex-1">
        <SkeletonBase className="h-4 w-32 mb-2" />
        <SkeletonBase className="h-3 w-24" />
      </div>
    </div>
    <SkeletonText lines={3} />
  </div>
);

// Message skeleton
export const SkeletonMessage = ({ isOwn = false, className = '' }) => (
  <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} ${className}`}>
    <div className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
      {!isOwn && <SkeletonAvatar size="sm" />}
      <div className="space-y-1">
        <SkeletonBase className={`h-16 ${isOwn ? 'w-48' : 'w-56'} rounded-2xl`} />
        <SkeletonBase className="h-3 w-16" />
      </div>
    </div>
  </div>
);

// Conversation list skeleton
export const SkeletonConversation = ({ className = '' }) => (
  <div className={`flex items-center gap-3 p-3 ${className}`}>
    <SkeletonAvatar size="md" />
    <div className="flex-1">
      <SkeletonBase className="h-4 w-32 mb-2" />
      <SkeletonBase className="h-3 w-48" />
    </div>
    <SkeletonBase className="h-5 w-5 rounded-full" />
  </div>
);

// Grid skeleton for streams/cards
export const SkeletonGrid = ({ count = 6, className = '' }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: i * 0.05 }}
      >
        <SkeletonCard />
      </motion.div>
    ))}
  </div>
);

// Stats skeleton
export const SkeletonStats = ({ count = 4, className = '' }) => (
  <div className={`grid grid-cols-2 sm:grid-cols-4 gap-4 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="p-4 rounded-2xl bg-[#08080C] border border-white/5">
        <SkeletonBase className="h-4 w-12 mb-2" />
        <SkeletonBase className="h-8 w-20 mb-1" />
        <SkeletonBase className="h-3 w-16" />
      </div>
    ))}
  </div>
);

// Table skeleton
export const SkeletonTable = ({ rows = 5, cols = 4, className = '' }) => (
  <div className={`rounded-2xl bg-[#08080C] border border-white/5 overflow-hidden ${className}`}>
    {/* Header */}
    <div className="flex gap-4 p-4 border-b border-white/5">
      {Array.from({ length: cols }).map((_, i) => (
        <SkeletonBase key={i} className="h-4 flex-1" />
      ))}
    </div>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="flex gap-4 p-4 border-b border-white/5 last:border-0">
        {Array.from({ length: cols }).map((_, colIndex) => (
          <SkeletonBase 
            key={colIndex} 
            className={`h-4 flex-1 ${colIndex === 0 ? 'w-1/4' : ''}`} 
          />
        ))}
      </div>
    ))}
  </div>
);

// Full page loading skeleton
export const SkeletonPage = ({ className = '' }) => (
  <div className={`p-6 space-y-6 ${className}`}>
    {/* Header */}
    <div className="flex items-center justify-between">
      <div>
        <SkeletonBase className="h-8 w-48 mb-2" />
        <SkeletonBase className="h-4 w-32" />
      </div>
      <SkeletonBase className="h-10 w-32 rounded-xl" />
    </div>
    
    {/* Stats */}
    <SkeletonStats count={4} />
    
    {/* Content */}
    <SkeletonGrid count={6} />
  </div>
);

// Inline loading spinner with glow
export const LoadingSpinner = ({ size = 'md', color = '#00F0FF', className = '' }) => {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  return (
    <div className={`relative ${sizes[size]} ${className}`}>
      <motion.div
        className="absolute inset-0 rounded-full"
        style={{ 
          border: `2px solid ${color}20`,
          borderTopColor: color
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
      <div 
        className="absolute inset-1 rounded-full"
        style={{ 
          background: `radial-gradient(circle, ${color}10 0%, transparent 70%)`,
          filter: `drop-shadow(0 0 8px ${color}60)`
        }}
      />
    </div>
  );
};

// Full screen loading overlay
export const LoadingOverlay = ({ message = 'Loading...', className = '' }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className={`fixed inset-0 z-50 flex items-center justify-center bg-[#030305]/90 backdrop-blur-sm ${className}`}
  >
    <div className="text-center">
      <LoadingSpinner size="xl" className="mx-auto mb-4" />
      <p className="text-gray-400 animate-pulse">{message}</p>
    </div>
  </motion.div>
);

// Dot loading animation
export const LoadingDots = ({ color = '#00F0FF', className = '' }) => (
  <div className={`flex gap-1 ${className}`}>
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        className="w-2 h-2 rounded-full"
        style={{ backgroundColor: color }}
        animate={{ 
          y: [-2, 2, -2],
          opacity: [0.5, 1, 0.5]
        }}
        transition={{ 
          duration: 0.6, 
          repeat: Infinity,
          delay: i * 0.15 
        }}
      />
    ))}
  </div>
);

export default SkeletonBase;
